/**
 * ViewLoader — transforms Tier 2 view JSON files into FormSchema.
 *
 * Pipeline: views/{entity}/domain.json + orch.json → FormSchema (mode='view')
 *
 * Key differences from FormLoader:
 *   - No rules.json (read-only, no effects/observers)
 *   - No validation (read-only)
 *   - Fields default to { minCols: 1, maxCols: 1 } (no pixel props)
 *   - Orch uses hybrid layout: composite nodes (tabs, surface, section, each, conditional, data)
 *     nest via `children`, leaf rows use `{ rowId, fields: [{ scope }] }`
 *   - Presentation keys (icon, layout, renderAs, etc.) live on orch fields
 *   - Behavioral keys (masked, unmaskActionId, etc.) live inline on domain properties
 *   - viewPresentation defaults to 'view-fields'
 */

import type {
  RawViewDomain,
  RawViewOrch,
  RawViewOrchLayoutEntry,
  RawViewOrchRow,
  RawViewOrchField,
  RawViewOrchTabsNode,
  RawViewOrchSurfaceNode,
  RawViewOrchSectionNode,
  RawViewOrchEachNode,
  RawViewOrchConditionalNode,
  RawViewOrchDataNode,
  RawViewOrchDividerNode,
  RawViewOrchCustomNode,
  RawViewOrchUdfNode,
  RawViewOrchFileListNode,
  RawViewOrchActionButtonNode,
  ViewSchemaFiles,
  ValidationIssue,
  RawDomainSchemaProperty,
  FormLoaderContext,
} from './types';
import { resolveRef } from './uriResolver';
import { resolveDomainRefs } from './resolveDomainRefs';
import { createDefaultContext } from './createDefaultContext';
import type { FormSchema, FormMetadata, FormMode } from '../types/schema';
import type { FormBlock, BlockSlot } from '../types/block';
import type { FormFieldMapping, FieldDef, FieldWhen } from '../types/fieldMapping';
import type { Condition } from '../types/conditions';
import { resolvePropertyDisplayFormat } from '../utils/displayFormatKeys';
import { buildShellPresentation } from '../utils/shellPresentation';
import type {
  FormLayoutNode,
  FormFieldsNode,
  FormSectionDividerEntry,
  FormTabsNode,
  FormSurfaceNode,
  FormSectionNode,
  FormEachNode,
  FormConditionalNode,
  FormDataNode,
  FormDividerNode,
  FormCustomNode,
  FormUdfNode,
  FormFileListNode,
  FormActionButtonNode,
} from '../types/layout';

export interface ViewSchemaResult {
  schema: FormSchema;
  issues: ValidationIssue[];
}

// ─── Helpers ─────────────────────────────────────────────────────────

function scopeToKey(scope: string): string {
  return scope.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
}

/**
 * Recursively collects field keys referenced by orch layout entries via `scope`.
 * Used by `each` to restrict `itemFields` to only explicitly referenced properties.
 */
function collectReferencedFieldKeys(entries: RawViewOrchLayoutEntry[]): Set<string> {
  const keys = new Set<string>();
  for (const field of collectOrchFields(entries)) {
    keys.add(scopeToKey(field.scope));
  }
  return keys;
}

/** Flatten orch field entries (including nested conditional/section children). */
function collectOrchFields(entries: RawViewOrchLayoutEntry[]): RawViewOrchField[] {
  const fields: RawViewOrchField[] = [];
  for (const entry of entries) {
    if ('fields' in entry && Array.isArray((entry as RawViewOrchRow).fields)) {
      fields.push(...(entry as RawViewOrchRow).fields);
    }
    if ('children' in entry && Array.isArray((entry as { children?: RawViewOrchLayoutEntry[] }).children)) {
      fields.push(...collectOrchFields((entry as { children: RawViewOrchLayoutEntry[] }).children));
    }
  }
  return fields;
}

function pointerToPath(pointer: string): string {
  return pointer.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
}

function normalizeConditionPaths(cond: Record<string, unknown>): Condition {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(cond)) {
    if (k === 'field' && typeof v === 'string' && v.startsWith('#/')) {
      out[k] = pointerToPath(v);
    } else if (k === 'allOf' || k === 'anyOf') {
      out[k] = (v as Record<string, unknown>[]).map((c) => normalizeConditionPaths(c));
    } else if (k === 'not' && typeof v === 'object' && v !== null) {
      out[k] = normalizeConditionPaths(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out as unknown as Condition;
}

function collectConditionWatchFields(cond: Record<string, unknown>): string[] {
  const fields = new Set<string>();

  function walk(obj: Record<string, unknown>): void {
    for (const [k, v] of Object.entries(obj)) {
      if (k === 'field' && typeof v === 'string') {
        fields.add(v.startsWith('#/') ? pointerToPath(v) : v);
      } else if ((k === 'allOf' || k === 'anyOf') && Array.isArray(v)) {
        for (const item of v) {
          if (typeof item === 'object' && item !== null) {
            walk(item as Record<string, unknown>);
          }
        }
      } else if (k === 'not' && typeof v === 'object' && v !== null) {
        walk(v as Record<string, unknown>);
      }
    }
  }

  walk(cond);
  return [...fields];
}

function wrapWithDomainHiddenWhen(
  prop: RawDomainSchemaProperty,
  node: FormLayoutNode,
  leadingNodes: FormLayoutNode[] = [],
): FormConditionalNode {
  const hiddenWhen = prop.hiddenWhen as Record<string, unknown>;
  const hiddenCondition = normalizeConditionPaths(hiddenWhen);
  const watchFields = collectConditionWatchFields(hiddenWhen);
  const children = leadingNodes.length > 0 ? [...leadingNodes, node] : [node];
  return {
    type: 'conditional',
    watch: watchFields.length <= 1 ? (watchFields[0] ?? '') : watchFields,
    condition: { not: hiddenCondition } as unknown as Condition,
    children,
  };
}

function hiddenWhenSignature(prop: RawDomainSchemaProperty | undefined): string | null {
  if (!prop?.hiddenWhen || typeof prop.hiddenWhen !== 'object') return null;
  return JSON.stringify(prop.hiddenWhen);
}

function buildViewFieldWhen(prop: RawDomainSchemaProperty): FieldWhen | undefined {
  if (!prop.hiddenWhen || typeof prop.hiddenWhen !== 'object') return undefined;
  return {
    visible: { not: normalizeConditionPaths(prop.hiddenWhen as Record<string, unknown>) } as unknown as Condition,
  };
}

/** Participant tax account sections — compile fallback when domain JSON metadata is incomplete. */
const PARTICIPANT_TAX_ACCOUNT_EACH: Record<
  string,
  { sectionTitle: string; sectionIcon: string; emptyState: { title: string } }
> = {
  brokerAccounts: {
    sectionTitle: 'Brokerage',
    sectionIcon: 'real_estate_agent',
    emptyState: { title: '-' },
  },
  transferAgentAccounts: {
    sectionTitle: 'Transfer Agent',
    sectionIcon: 'support_agent',
    emptyState: { title: '-' },
  },
};

function resolveTitleFrom(
  prop: RawDomainSchemaProperty,
  orchField: RawViewOrchField,
): Record<string, unknown> | undefined {
  const titleFromKey = orchField.overrideLabelKey ?? orchField.titleFrom;
  if (typeof titleFromKey !== 'string' || titleFromKey.length === 0) return undefined;
  return { titleFrom: titleFromKey };
}

function wrapEachWithDomainSection(
  eachNode: FormEachNode,
  dataKey: string,
  arrayProp: RawDomainSchemaProperty | undefined,
  orchEachNode?: RawViewOrchEachNode,
): FormLayoutNode {
  const known = PARTICIPANT_TAX_ACCOUNT_EACH[dataKey];
  const title = arrayProp?.title ?? known?.sectionTitle;
  if (!title) return eachNode;
  const sectionIcon = orchEachNode?.sectionIcon ?? known?.sectionIcon;
  const headerVariant = orchEachNode?.sectionHeaderVariant ?? 'heading';
  const divider = orchEachNode?.sectionDivider ?? false;
  const sectionNode: FormSectionNode = {
    type: 'section',
    title,
    ...(sectionIcon != null ? { icon: sectionIcon } : {}),
    headerVariant,
    divider,
    children: [eachNode],
  };
  return sectionNode;
}

function viewPropertyToFieldDef(prop: RawDomainSchemaProperty): FieldDef {
  const displayFormat = resolvePropertyDisplayFormat(prop);
  const statusMap = (prop as Record<string, unknown>).statusMap as Record<string, { icon?: string; tone?: string; label?: string }> | undefined;
  const emptyText = (prop as Record<string, unknown>).emptyText as string | undefined;
  const adapterHint = (prop as Record<string, unknown>).adapterHint as string | undefined;
  const badgeTemplate = (prop as Record<string, unknown>).badgeTemplate as string | undefined;
  const badgeVariant = (prop as Record<string, unknown>).badgeVariant as string | undefined;
  const displayPrecision = (prop as Record<string, unknown>).displayPrecision as number | undefined;

  const isMultiline = displayFormat === 'multiline' || displayFormat === 'multiline-list';
  const schemaKeys = isMultiline && prop.properties != null
    ? Object.keys(prop.properties as Record<string, unknown>)
    : undefined;
  const itemSchemaKeys = isMultiline && prop.items != null
    ? Object.keys((prop.items as Record<string, unknown>).properties ?? {})
    : undefined;
  const multilineKeys = schemaKeys ?? itemSchemaKeys;

  return {
    label: prop.title ?? '',
    adapterHint: adapterHint ?? (prop.type === 'boolean' ? 'checkbox' : 'text'),
    schema: {},
    ...(displayFormat != null && { displayFormat }),
    ...(displayPrecision != null && { displayPrecision }),
    ...(statusMap != null && { statusMap }),
    ...(emptyText != null && { emptyText }),
    ...(badgeTemplate != null && { badgeTemplate }),
    ...(badgeVariant != null && { badgeVariant }),
    ...(multilineKeys != null && { adapterProps: { multilineKeys } }),
  };
}

// ─── Discriminator: is this a leaf row or a composite node? ──────────

function isLeafRow(entry: RawViewOrchLayoutEntry): entry is RawViewOrchRow {
  return 'rowId' in entry && 'fields' in entry;
}

// ─── Build field mapping from a view orch scope + view domain ────────

/**
 * Build a FormFieldMapping from an orch field scope + domain property.
 * Presentation keys come from the orch field; behavioral keys from the domain property.
 */
function buildViewFieldMapping(
  orchField: RawViewOrchField,
  properties: Record<string, RawDomainSchemaProperty>,
  issues: ValidationIssue[],
): FormFieldMapping | null {
  const key = scopeToKey(orchField.scope);
  const prop = properties[key];

  if (!prop) {
    issues.push({
      severity: 'warning',
      path: orchField.scope,
      message: `[view-orch] scope "${orchField.scope}" references unknown property in view domain`,
    });
    return null;
  }

  if (
    '$ref' in prop
    && typeof (prop as Record<string, unknown>).$ref === 'string'
    && prop.title == null
  ) {
    issues.push({
      severity: 'warning',
      path: orchField.scope,
      message: `[view-domain] unresolved $ref "${(prop as Record<string, unknown>).$ref}" for property "${key}" — check module registry / participant.domain.json`,
    });
  }

  const def = viewPropertyToFieldDef(prop);

  // Presentation keys from orch
  const icon = orchField.icon;
  const layout = orchField.layout;
  const renderAs = orchField.renderAs;
  const truncate = orchField.truncate;
  const overridePrecision = orchField.overridePrecision;
  const suffix = orchField.suffix;
  const extras = orchField.extras;
  const size = orchField.size;

  // Behavioral keys from domain property
  const masked = prop.masked;
  const maskedPlaceholder = prop.maskedPlaceholder;
  const unmaskActionId = prop.unmaskActionId;
  const maskedFormatKey = prop.maskedFormatKey;

  // Merge domain `assembleFrom` into extras so ViewField can resolve composite addresses
  const domainAssembleFrom = (prop as Record<string, unknown>).assembleFrom as Record<string, string> | undefined;
  const mergedExtras = domainAssembleFrom != null
    ? { ...(extras ?? {}), assembleFrom: domainAssembleFrom }
    : extras;

  const viewDisplayOverrides: Record<string, unknown> = {};
  if (masked != null) viewDisplayOverrides.masked = masked;
  if (maskedPlaceholder != null) viewDisplayOverrides.maskedPlaceholder = maskedPlaceholder;
  if (unmaskActionId != null) viewDisplayOverrides.unmaskActionId = unmaskActionId;
  if (maskedFormatKey != null) viewDisplayOverrides.maskedFormatKey = maskedFormatKey;
  if (icon != null) viewDisplayOverrides.icon = icon;
  if (layout != null) viewDisplayOverrides.layout = layout;
  if (renderAs != null) viewDisplayOverrides.renderAs = renderAs;
  if (truncate != null) viewDisplayOverrides.truncate = truncate;
  if (overridePrecision != null) viewDisplayOverrides.overridePrecision = overridePrecision;
  if (suffix != null) viewDisplayOverrides.suffix = suffix;
  if (mergedExtras != null) viewDisplayOverrides.extras = mergedExtras;
  if (size != null) viewDisplayOverrides.size = size;

  const fieldWhen = buildViewFieldWhen(prop);
  const titleFromProps = resolveTitleFrom(prop, orchField);
  const resolvedOverrideLabel = orchField.overrideLabel;
  const shellPresentation = buildShellPresentation(orchField);

  return {
    name: key,
    def,
    required: false,
    matrix: {
      minCols: orchField.minCols ?? 1,
      maxCols: orchField.maxCols ?? 1,
      ...(orchField.minWidthPx != null && { minWidthPx: orchField.minWidthPx }),
      ...(orchField.maxWidthPx != null && { maxWidthPx: orchField.maxWidthPx }),
      ...(orchField.startNewRow === true && { startNewRow: true }),
    },
    ...(resolvedOverrideLabel != null && { overrideLabel: resolvedOverrideLabel }),
    ...(Object.keys(viewDisplayOverrides).length > 0 && { viewDisplayOverrides }),
    ...(fieldWhen != null && { when: fieldWhen }),
    ...(titleFromProps != null && { adapterProps: titleFromProps }),
    ...(shellPresentation != null && { shellPresentation }),
  };
}

// ─── Build a leaf row (rowId + fields) → FormFieldsNode ──────────────

function buildViewRow(
  row: RawViewOrchRow,
  properties: Record<string, RawDomainSchemaProperty>,
  issues: ValidationIssue[],
): FormFieldsNode {
  const fields: (FormFieldMapping | FormSectionDividerEntry)[] = [];

  if (row.title) {
    fields.push({
      type: 'section-divider',
      title: row.title,
      titleVariant: 'heading',
      divider: false,
    });
  }

  for (const orchField of row.fields) {
    const mapping = buildViewFieldMapping(orchField, properties, issues);
    if (mapping) {
      if (row.hideLabel === true) {
        mapping.viewDisplayOverrides = { ...mapping.viewDisplayOverrides, hideLabel: true };
      }
      fields.push(mapping);
    }
  }

  return {
    type: 'fields',
    fields,
    ...(row.columns != null && { columns: row.columns }),
  };
}

// ─── Recursive: view orch entry → FormLayoutNode ─────────────────────

function buildViewLayoutNode(
  entry: RawViewOrchLayoutEntry,
  properties: Record<string, RawDomainSchemaProperty>,
  issues: ValidationIssue[],
): FormLayoutNode | null {
  if (isLeafRow(entry)) {
    return buildViewRow(entry, properties, issues);
  }

  switch (entry.type) {
    case 'tabs': {
      const tabsEntry = entry as RawViewOrchTabsNode;
      const node: FormTabsNode = {
        type: 'tabs',
        ...(tabsEntry.semanticId != null && { semanticId: tabsEntry.semanticId }),
        ...(tabsEntry.centered != null && { centered: tabsEntry.centered }),
        tabs: tabsEntry.tabs.map((tab) => ({
          id: tab.id,
          label: tab.label,
          children: buildViewLayoutChildren(tab.children, properties, issues),
        })),
      };
      return node;
    }

    case 'surface': {
      const surfaceEntry = entry as RawViewOrchSurfaceNode;
      const node: FormSurfaceNode = {
        type: 'surface',
        ...(surfaceEntry.variant != null && { variant: surfaceEntry.variant }),
        ...(surfaceEntry.tone != null && { tone: surfaceEntry.tone as FormSurfaceNode['tone'] }),
        ...(surfaceEntry.bordered != null && { bordered: surfaceEntry.bordered }),
        ...(surfaceEntry.padding != null && { padding: surfaceEntry.padding }),
        ...(surfaceEntry.semanticId != null && { semanticId: surfaceEntry.semanticId }),
        ...(surfaceEntry.flush != null && { flush: surfaceEntry.flush }),
        children: buildViewLayoutChildren(surfaceEntry.children, properties, issues),
      };
      return node;
    }

    case 'section': {
      const sectionEntry = entry as RawViewOrchSectionNode;
      const node: FormSectionNode = {
        type: 'section',
        ...(sectionEntry.title != null && { title: sectionEntry.title }),
        ...(sectionEntry.subtitle != null && { subtitle: sectionEntry.subtitle }),
        ...(sectionEntry.semanticId != null && { semanticId: sectionEntry.semanticId }),
        ...(sectionEntry.icon != null && { icon: sectionEntry.icon }),
        ...(sectionEntry.headerAction != null && { headerAction: sectionEntry.headerAction }),
        ...(sectionEntry.headerVariant != null && { headerVariant: sectionEntry.headerVariant }),
        ...(sectionEntry.divider != null && { divider: sectionEntry.divider }),
        ...(sectionEntry.className != null && { className: sectionEntry.className }),
        children: buildViewLayoutChildren(sectionEntry.children, properties, issues),
      };
      return node;
    }

    case 'each': {
      return buildRawViewEachNode(entry as RawViewOrchEachNode, properties, issues);
    }

    case 'conditional': {
      issues.push({
        severity: 'warning',
        path: 'orch.layout',
        message: '[view-orch] conditional nodes are deprecated — use hiddenWhen on domain.json properties instead',
      });
      const condEntry = entry as RawViewOrchConditionalNode;
      const conditionObj =
        condEntry.condition === 'notEmpty' || condEntry.condition === 'exists'
          ? { field: condEntry.when, notEmpty: true }
          : condEntry.condition === 'isEmpty'
            ? { field: condEntry.when, isEmpty: true }
            : condEntry.condition === 'notEquals'
              ? { field: condEntry.when, notEquals: condEntry.conditionValue }
              : condEntry.condition === 'equals'
                ? { field: condEntry.when, equals: condEntry.conditionValue }
                : { field: condEntry.when, notEmpty: true };

      const node: FormConditionalNode = {
        type: 'conditional',
        watch: condEntry.when,
        condition: conditionObj as any,
        children: buildViewLayoutChildren(condEntry.children, properties, issues),
      };
      return node;
    }

    case 'data': {
      const dataEntry = entry as RawViewOrchDataNode;
      const node: FormDataNode = {
        type: 'data',
        dataKey: dataEntry.dataKey,
        ...(dataEntry.fetch != null && { fetch: dataEntry.fetch }),
        children: buildViewLayoutChildren(dataEntry.children, properties, issues),
      };
      return node;
    }

    case 'divider': {
      const node: FormDividerNode = { type: 'divider' };
      return node;
    }

    case 'custom': {
      const customEntry = entry as RawViewOrchCustomNode;
      const node: FormCustomNode = {
        type: 'custom',
        rendererKey: customEntry.rendererKey,
        ...(customEntry.props != null && { props: customEntry.props }),
      };
      return node;
    }

    case 'udf': {
      const udfEntry = entry as RawViewOrchUdfNode;
      const node: FormUdfNode = {
        type: 'udf',
        moduleId: udfEntry.moduleId,
        actionId: udfEntry.actionId,
        ...(udfEntry.title != null && { title: udfEntry.title }),
        ...(udfEntry.titleStyle != null && { titleStyle: udfEntry.titleStyle }),
        ...(udfEntry.columns != null && { columns: udfEntry.columns }),
        ...(udfEntry.fieldPrefix != null && { fieldPrefix: udfEntry.fieldPrefix }),
      };
      return node;
    }

    case 'file-list': {
      const fileListEntry = entry as RawViewOrchFileListNode;
      return {
        type: 'file-list',
        dataKey: fileListEntry.dataKey,
        ...(fileListEntry.variant != null && { variant: fileListEntry.variant }),
      };
    }

    case 'action-button': {
      const abEntry = entry as RawViewOrchActionButtonNode;
      if (abEntry.scope) {
        const key = scopeToKey(abEntry.scope);
        const prop = properties[key];
        const actionId = (prop as Record<string, unknown> | undefined)?.actionId as string | undefined;
        if (!prop || !actionId) {
          issues.push({
            severity: 'warning',
            path: abEntry.scope,
            message: `[view-orch] action-button scope "${abEntry.scope}" requires domain actionId`,
          });
          return null;
        }
        const node: FormActionButtonNode = {
          type: 'action-button',
          actionId,
          label: prop.title ?? abEntry.label ?? '',
          icon: abEntry.icon,
          variant: abEntry.variant,
          ...(abEntry.align != null && { align: abEntry.align }),
        };
        if (prop.hiddenWhen && typeof prop.hiddenWhen === 'object') {
          return wrapWithDomainHiddenWhen(prop, node);
        }
        return node;
      }

      if (!abEntry.actionId || !abEntry.label) {
        issues.push({
          severity: 'warning',
          path: 'orch.layout',
          message: '[view-orch] action-button requires scope or inline actionId + label',
        });
        return null;
      }

      const node: FormActionButtonNode = {
        type: 'action-button',
        actionId: abEntry.actionId,
        label: abEntry.label,
        ...(abEntry.icon != null && { icon: abEntry.icon }),
        ...(abEntry.variant != null && { variant: abEntry.variant }),
        ...(abEntry.align != null && { align: abEntry.align }),
      };
      return node;
    }

    default:
      issues.push({
        severity: 'warning',
        path: 'orch.layout',
        message: `[view-orch] unknown layout entry type: ${(entry as { type?: string }).type}`,
      });
      return null;
  }
}

/**
 * Resolve view `each` empty placeholder.
 * Priority: domain property `emptyState` → orch `each` node → titled-array default → hardcoded fallback.
 */
function resolveViewEachEmptyState(
  eachEntry: RawViewOrchEachNode,
  arrayProp: RawDomainSchemaProperty | undefined,
): { title: string; description?: string } | undefined {
  const explicit = arrayProp?.emptyState ?? eachEntry.emptyState;
  if (explicit != null) return explicit;

  if (
    arrayProp?.type === 'array'
    && arrayProp.title
    && Array.isArray(arrayProp.default)
    && arrayProp.default.length === 0
  ) {
    return { title: '-' };
  }

  const known = PARTICIPANT_TAX_ACCOUNT_EACH[eachEntry.dataKey];
  if (known) return known.emptyState;

  return undefined;
}

function buildRawViewEachNode(
  eachEntry: RawViewOrchEachNode,
  properties: Record<string, RawDomainSchemaProperty>,
  issues: ValidationIssue[],
): FormEachNode {
  const arrayProp = properties[eachEntry.dataKey];
  const itemsSchema = arrayProp?.items as RawDomainSchemaProperty | undefined;
  const itemProps = (itemsSchema?.properties ?? {}) as Record<string, RawDomainSchemaProperty>;

  const itemFieldsFromOrch: FormFieldMapping[] = [];
  const orchFieldEntries = collectOrchFields(eachEntry.children);
  if (orchFieldEntries.length > 0) {
    for (const orchField of orchFieldEntries) {
      const mapping = buildViewFieldMapping(orchField, itemProps, issues);
      if (mapping) itemFieldsFromOrch.push(mapping);
    }
  } else {
    for (const [fieldKey, fieldProp] of Object.entries(itemProps)) {
      const def = viewPropertyToFieldDef(fieldProp);
      itemFieldsFromOrch.push({
        name: fieldKey,
        def,
        required: false,
        matrix: { minCols: 1, maxCols: 1 },
      });
    }
  }

  // Propagate row-level `hideLabel` from each children rows to field overrides.
  // collectOrchFields strips the parent row — recover it here.
  for (const entry of eachEntry.children) {
    if ('fields' in entry && (entry as RawViewOrchRow).hideLabel === true) {
      const row = entry as RawViewOrchRow;
      for (const orchField of row.fields) {
        const key = scopeToKey(orchField.scope);
        const mapping = itemFieldsFromOrch.find((f) => f.name === key);
        if (mapping) {
          mapping.viewDisplayOverrides = { ...mapping.viewDisplayOverrides, hideLabel: true };
        }
      }
    }
  }

  // Hoist `columns` from a single child row to the each node level
  // so FormEachDefaultRenderer picks it up.
  let resolvedColumns = eachEntry.columns;
  if (resolvedColumns == null) {
    const leafRows = eachEntry.children.filter(
      (e) => 'fields' in e && 'rowId' in e,
    ) as RawViewOrchRow[];
    if (leafRows.length === 1 && leafRows[0].columns != null) {
      resolvedColumns = leafRows[0].columns;
    }
  }

  const eachChildren = buildViewLayoutChildren(eachEntry.children, itemProps, issues);
  const resolvedEmptyState = resolveViewEachEmptyState(eachEntry, arrayProp);

  return {
    type: 'each',
    name: eachEntry.dataKey,
    itemFields: itemFieldsFromOrch,
    ...(eachEntry.className != null && { className: eachEntry.className }),
    ...(eachEntry.itemClassName != null && { itemClassName: eachEntry.itemClassName }),
    ...(eachEntry.presentation != null && { presentation: eachEntry.presentation }),
    ...(resolvedColumns != null && { columns: resolvedColumns }),
    ...(resolvedEmptyState != null && { emptyState: resolvedEmptyState }),
    ...(eachChildren.length > 0 && { itemLayout: eachChildren }),
  };
}

function finalizeEachLayoutNode(
  eachEntry: RawViewOrchEachNode,
  eachNode: FormEachNode,
  properties: Record<string, RawDomainSchemaProperty>,
  options?: { skipHiddenWhen?: boolean },
): FormLayoutNode {
  const arrayProp = properties[eachEntry.dataKey];
  let node: FormLayoutNode = wrapEachWithDomainSection(eachNode, eachEntry.dataKey, arrayProp, eachEntry);
  if (!options?.skipHiddenWhen && arrayProp?.hiddenWhen && typeof arrayProp.hiddenWhen === 'object') {
    node = wrapWithDomainHiddenWhen(arrayProp, node);
  }
  return node;
}

function finalizeTopLevelLayoutNode(
  entry: RawViewOrchLayoutEntry,
  node: FormLayoutNode,
  properties: Record<string, RawDomainSchemaProperty>,
): FormLayoutNode {
  if ('type' in entry && entry.type === 'each') {
    return finalizeEachLayoutNode(entry as RawViewOrchEachNode, node as FormEachNode, properties);
  }

  if ('type' in entry && entry.type === 'file-list') {
    const prop = properties[(entry as RawViewOrchFileListNode).dataKey];
    if (prop?.hiddenWhen && typeof prop.hiddenWhen === 'object') {
      return wrapWithDomainHiddenWhen(prop, node);
    }
  }

  return node;
}

function buildViewLayoutChildren(
  entries: RawViewOrchLayoutEntry[],
  properties: Record<string, RawDomainSchemaProperty>,
  issues: ValidationIssue[],
): FormLayoutNode[] {
  const result: FormLayoutNode[] = [];

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const nextEntry = entries[i + 1];

    if ('type' in entry && entry.type === 'divider' && nextEntry != null) {
      if ('type' in nextEntry && nextEntry.type === 'each') {
        const eachEntry = nextEntry as RawViewOrchEachNode;
        const arrayProp = properties[eachEntry.dataKey];
        if (hiddenWhenSignature(arrayProp)) {
          const eachNode = buildRawViewEachNode(eachEntry, properties, issues);
          const inner = finalizeEachLayoutNode(eachEntry, eachNode, properties, {
            skipHiddenWhen: true,
          });
          result.push(wrapWithDomainHiddenWhen(arrayProp!, inner, [{ type: 'divider' }]));
          i += 1;
          continue;
        }
      }

      if (isLeafRow(nextEntry)) {
        const rowHiddenWhen = hiddenWhenSignature(properties[scopeToKey(nextEntry.fields[0]?.scope ?? '')]);
        const allFieldsShareHiddenWhen =
          rowHiddenWhen != null
          && nextEntry.fields.every((field) => hiddenWhenSignature(properties[scopeToKey(field.scope)]) === rowHiddenWhen);
        if (allFieldsShareHiddenWhen) {
          const rowNode = buildViewLayoutNode(nextEntry, properties, issues);
          if (rowNode) {
            const firstProp = properties[scopeToKey(nextEntry.fields[0].scope)];
            result.push(wrapWithDomainHiddenWhen(firstProp!, rowNode, [{ type: 'divider' }]));
            i += 1;
            continue;
          }
        }
      }
    }

    const node = buildViewLayoutNode(entry, properties, issues);
    if (node) {
      result.push(finalizeTopLevelLayoutNode(entry, node, properties));
    }
  }

  return result;
}

function collectDomainPropertyDefaults(
  properties: Record<string, RawDomainSchemaProperty>,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  for (const [key, prop] of Object.entries(properties)) {
    if (prop.default !== undefined) {
      defaults[key] = prop.default;
    }
  }
  return defaults;
}

// ─── Main entry point ────────────────────────────────────────────────

export function loadViewSchema(
  files: ViewSchemaFiles,
  ctx: FormLoaderContext = createDefaultContext(),
): ViewSchemaResult {
  const issues: ValidationIssue[] = [];
  const { domain, orch } = files;

  const rawProperties = (domain.properties ?? {}) as Record<string, unknown>;
  const resolvedProperties = resolveDomainRefs(
    rawProperties,
    ctx,
    domain as unknown as Record<string, unknown>,
  );
  const properties = resolvedProperties as Record<string, RawDomainSchemaProperty>;
  const viewMode = domain.modes?.view;

  const layoutNodes = buildViewLayoutChildren(orch.layout, properties, issues);

  // Use a stable id for the block, but do NOT namespace view schemas.
  // View field names are flat (e.g. "dateOfBirth", not "details.dateOfBirth").
  // If a namespace were set, hydratePayload would wrap data as { details: { ... } }
  // and form.reset() would find no matching fields — data would silently not render.
  const blockId = orch.semanticId ?? domain.$id?.split('/').pop() ?? 'view';

  const block: FormBlock = {
    id: blockId,
    layout: layoutNodes,
  };

  const blocks: BlockSlot[] = [{ use: block }];

  const metadata: FormMetadata = {
    entity: domain.title ?? domain.$id ?? 'View',
    title: domain.title ?? 'Detail',
    description: domain.description,
    semanticId: orch.semanticId ?? domain.$id ?? 'view',
  };

  const domainDefaults = collectDomainPropertyDefaults(properties);

  // Root-level actionId > modes.view.fetchActionId (backward compat)
  const resolvedActionId = domain.actionId ?? viewMode?.fetchActionId;

  const modes: Record<string, FormMode> = {
    view: {
      overrides: { readOnly: true },
      viewPresentation: viewMode?.viewPresentation ?? 'view-fields',
      ...(Object.keys(domainDefaults).length > 0 && { defaultValues: domainDefaults }),
      ...(resolvedActionId != null && {
        fetchActionId: resolvedActionId,
      }),
      ...(viewMode?.dataSource != null && {
        dataSource: viewMode.dataSource as any,
      }),
    },
  };

  const schema: FormSchema = {
    metadata,
    blocks,
    modes,
  };

  return { schema, issues };
}
